import { memo, useState } from 'react';

import {
  BtnGroup,
  BtnToolbar,
  Button,
  ButtonAction,
  Col,
  Combobox,
  formatNumber,
  formattedCell,
  type HookFormMethods,
  HookFormResolver,
  PageTitle,
  Row,
  TaxpayerCell,
  Text
} from '@dec/shared';
import { type ColDef, type ValueFormatterParams } from 'ag-grid-community';
import { ActionsGrid } from 'app/components/grid';
import { type ActionColumn } from 'app/components/grid/actions-column';
import GridApplication from 'app/components/grid/gridApplication';
import { catalogs } from 'app/modules/Parameters/Catalog/Catalog/services/catalog';
import { useCatalogList } from 'app/modules/Parameters/Catalog/Catalog/services/useCatalog';
import { useGetAllTaxes } from 'app/modules/taxes/hooks/useTaxes';
import * as Icon from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import env from '@/env';

import { useJurisdictions } from '../hooks/useJurisdictions';
import { type CurrentAccountJurisdictionSummary } from '../models/CurrentAccount';
import { type SearchAmendingCredit } from '../models/types';

interface Props {}

const validations = yup.object().shape({
  // tin: yup.string().required(i18next.t(fieldRequired))
});

export const CurrentAccountJurisdictionPage = memo((props: Props) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const actions: Array<ActionColumn> = [
    {
      icon: Icon.Search,
      variant: 'secondary',
      onClick: async (row: CurrentAccountJurisdictionSummary) => {
        navigate(`/current-account/jurisdiction/${row.id}/transactions`);
      }
    }
  ];
  const colDef: Array<ColDef> = [
    {
      headerName: t('taxpayer-taxreturns.header.actions'),
      cellRenderer: ActionsGrid,
      cellRendererParams: {
        actions: actions
      }
    },
    {
      headerName: t('common.label.jurisdiction'),
      field: 'jurisdictionName'
    },

    {
      headerName: t('config.tin'),
      field: 'tin',
      ...formattedCell((params: ValueFormatterParams) => {
        return params.data.tin;
      }, TaxpayerCell)
    },
    {
      headerName: t('taxpayer-current-account.label.taxpayerName'),
      field: 'taxpayer',
      valueFormatter: (params: ValueFormatterParams) => {
        return params.data.taxpayerName;
      }
    },
    {
      headerName: t('taxpayer-current-account.label.tax'),
      field: 'taxname',
      valueFormatter: (params: ValueFormatterParams) => {
        return params.data.taxName;
      }
    },
    {
      headerName: t('taxpayer-current-account.label.accountType'),
      field: 'accountType',
      valueFormatter: (params: ValueFormatterParams) => {
        return params.data.accountTypeName;
      }
    },

    {
      headerName: t('taxpayer-current-account.label.balance'),
      field: 'balance',
      cellStyle: (params: any) => {
        return { 'text-align': 'right' };
      },
      valueFormatter: (params: ValueFormatterParams) => {
        return formatNumber(params.value);
      }
    }
  ];
  const [search, setSearch] = useState({} as SearchAmendingCredit);
  const { data: forms, loading: loadingForms, error: errorTaxes } = useGetAllTaxes();
  const [{ data: accountTypes, loading, error }] = useCatalogList(catalogs.accountType);
  const { data: jurisdictions } = useJurisdictions();

  const onSearch = (values: SearchAmendingCredit) => {
    if (values.tax) {
      values.taxId = values.tax.id;
    }
    if (values.accountType) {
      values.accountTypeId = values.accountType.id;
    }
    setSearch(values);
  };
  if (loadingForms || loading) {
    return <></>;
  }
  return (
    <>
      <PageTitle
        title={t('taxpayer-current-account.title.currentAccountJurisdiction')}
        subtitle={t('taxpayer-current-account.subtitle.currentAccountJurisdiction')}
        icon={Icon.BoxSeam}
      />

      <HookFormResolver validations={validations} onSubmit={onSearch}>
        {({ formState: { errors }, reset, setValue, control }: HookFormMethods) => {
          return (
            <>
              <Row>
                <Col>
                  <Text label={t('config.tin')} name={'tin'} />
                </Col>
                <Col>
                  <Combobox
                    data={forms}
                    code={'id'}
                    display={'shortName'}
                    label={t('taxpayer-current-account.label.tax')}
                    name="tax"
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <Combobox
                    data={accountTypes}
                    code={'id'}
                    display={'name'}
                    label={t('taxpayer-current-account.label.accountType')}
                    name="accountType"
                  />
                </Col>
                <Col>
                  <Combobox
                    data={jurisdictions}
                    code={'id'}
                    display={'name'}
                    label={t('common.label.jurisdiction')}
                    name="jurisdiction"
                  />
                </Col>
              </Row>
              <BtnToolbar>
                <BtnGroup>
                  <Button
                    action={ButtonAction.clean}
                    type={'reset'}
                    onClick={() => {
                      setSearch({});
                      reset({
                        tax: null,
                        accountType: null
                      });
                    }}
                  />
                </BtnGroup>
                <BtnGroup>
                  <Button action={ButtonAction.find} type={'submit'} variant={'primary'} />
                </BtnGroup>
              </BtnToolbar>
            </>
          );
        }}
      </HookFormResolver>
      <Row>
        <Col>
          <GridApplication
            url={`${env.publicUrl}/current-account/accounts/jurisdiction`}
            columnDefs={colDef}
            search={search}
          />
        </Col>
      </Row>
    </>
  );
});

CurrentAccountJurisdictionPage.displayName = 'CurrentAccountJurisdictionPage';
